import {CalloutIcon} from '@wandb/components';
import React, {type FC, useCallback, useEffect, useRef} from 'react';

import {truncateComponentStack} from '../../util/datadog/componentStack';
import {recordRumError} from '../../util/datadog/datadogRum';
import {ErrorBoundary} from '../ErrorBoundary';
import {useWBAgentWindowStore} from './window/useWBAgentWindowStore';

/**
 * ARIA renders behind two boundaries, so no crash inside it reaches
 * `AppErrorBoundary` and replaces the whole app with the error page:
 *
 * - `WBAgentErrorBoundary` wraps the panel body, the conversation included. It
 *   costs the body only, so the header and history sidebar stay usable and the
 *   fallback can offer a remount in place.
 * - `WBAgentDrawerErrorBoundary` is the last-resort catch-all around the whole
 *   ARIA surface, for a crash in the chrome or the AUI runtime that the inner
 *   boundary never sees.
 *
 * Crashes report to RUM instead of opening the crash report dialog, so a
 * contained failure is queryable without interrupting the user.
 */
export const WBAgentErrorBoundary: FC<{children: React.ReactNode}> = ({
  children,
}) => (
  <ReportingErrorBoundary
    reportAs="aria_chat_crash"
    renderFallback={() => <CrashFallback />}>
    {children}
  </ReportingErrorBoundary>
);

/**
 * Catches what the drawer's inner boundary cannot. A crash closes the panel —
 * its fallback has nowhere to render, since the drawer portals itself out of
 * this subtree — and reopening it mounts ARIA again from scratch.
 */
export const WBAgentDrawerErrorBoundary: FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const handleError = useCallback(() => {
    useWBAgentWindowStore.getState().setOpen(false);
  }, []);

  return (
    <ReportingErrorBoundary
      reportAs="aria_chat_surface_crash"
      onError={handleError}
      renderFallback={resetError => <RemountOnReopen onReopen={resetError} />}>
      {children}
    </ReportingErrorBoundary>
  );
};

const ReportingErrorBoundary: FC<{
  children: React.ReactNode;
  /** RUM message the crash reports under, so the layers stay distinguishable. */
  reportAs: string;
  renderFallback: (resetError: () => void) => React.ReactElement;
  onError?: () => void;
}> = ({children, reportAs, renderFallback, onError}) => {
  const handleError = useCallback(
    (error: Error, info: React.ErrorInfo) => {
      recordRumError(error, {
        message: reportAs,
        componentStack: truncateComponentStack(info.componentStack),
      });
      onError?.();
    },
    [reportAs, onError]
  );

  const handleRenderError = useCallback(
    (_error: Error, resetError: () => void) => renderFallback(resetError),
    [renderFallback]
  );

  return (
    <ErrorBoundary silent onError={handleError} renderError={handleRenderError}>
      {children}
    </ErrorBoundary>
  );
};

/** Mirrors the thread's own load-failure state, minus its retry affordance. */
const CrashFallback: FC = () => (
  <div
    role="alert"
    className="flex flex-1 flex-col items-center justify-center gap-12 px-16">
    <CalloutIcon icon="warning" color="red" size="md" />
    <span className="font-semibold text-base">Something went wrong</span>
  </div>
);

/** Clears a crashed drawer subtree the next time the user opens the panel. */
const RemountOnReopen: FC<{onReopen: () => void}> = ({onReopen}) => {
  const open = useWBAgentWindowStore(s => s.open);
  const wasOpen = useRef(open);

  useEffect(() => {
    if (open && !wasOpen.current) {
      onReopen();
    }
    wasOpen.current = open;
  }, [open, onReopen]);

  return null;
};
